import { Box, Card, FormControl, IconButton, InputLabel, MenuItem, Tooltip } from "@mui/material";
import { useAppSelector } from "redux/hooks";
import {
  selectLensEditorFieldAiConfigs,
  selectProfileBuilderAiMode,
  selectProfileBuilderFields,
  selectProfileBuilderSortedSections,
  updateFormField,
  updateFormFields,
  updateFormSections,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { SectionFieldsList } from "./SectionFieldsList";
import { swapElements } from "helpers/drag-and-drop";
import { FormSection } from "Backend";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useEffect, useState } from "react";
import { FormField } from "generated/graphql";
import { Add, DragIndicator, Edit } from "@mui/icons-material";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import theme from "theme";
import EditSectionDialog from "./EditSectionDialog";
import { useNavigate } from "react-router";
import PSelect from "components/PSelect";
import NewFormFieldDialog from "routes/lens-editor/profile-builder/NewFormFieldDialog";
import { selectFormPages } from "redux/slices/form.slice";
import NewFormSectionDialog from "routes/lens-editor/profile-builder/NewFormSectionDialog";
import EditPagesDialog from "routes/lens-editor/profile-builder/EditPagesDialog";

const ProfileBuilderFieldOutline = () => {
  const sections = useAppSelector(selectProfileBuilderSortedSections);
  const fields = useAppSelector(selectProfileBuilderFields);
  const fieldAiConfigs = useAppSelector(selectLensEditorFieldAiConfigs);
  const aiMode = useAppSelector(selectProfileBuilderAiMode);
  const snackpatch = useAppDispatchWithSnackbar();
  const [displaySections, setDisplaySections] = useState<FormSection[]>([]);
  const [displayFields, setDisplayFields] = useState<FormField[]>([]);
  const { sectionId } = useParamNumbers(["sectionId"]);
  const [editSection, setEditSection] = useState<FormSection | undefined>(undefined);
  const [showNewFormSection, setShowNewFormSection] = useState<boolean>(false);

  const navigate = useNavigate();
  const [showNewFieldDialogSectionId, setShowNewFieldDialogSectionId] = useState<number | undefined>();
  const formPages = useAppSelector(selectFormPages);
  const [pageFilter, setPageFilter] = useState<number>(-1);
  const [showEditPagesDialog, setShowEditPagesDialog] = useState<boolean>(false);
  const handleOpenNewField = (evt, sectionId: number) => {
    evt.stopPropagation();
    setShowNewFieldDialogSectionId(sectionId);
  };

  // eager ui update for reordering
  useEffect(() => {
    setDisplaySections(sections);
  }, [sections]);

  useEffect(() => {
    setDisplayFields(fields);
  }, [fields]);

  const handleEditSection = (evt, section: FormSection) => {
    evt.stopPropagation();
    setEditSection(section);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === "section") {
      const reorderedSections = swapElements<FormSection>(sections, sourceIndex, destIndex);
      setDisplaySections(reorderedSections);
      snackpatch(
        updateFormSections({
          sections: reorderedSections.map((s, i) => ({
            sectionId: s.id,
            values: {
              rank: i,
            },
          })),
        }),
        {
          successMessage: "Sections reordered successfully",
          errorMessage: "Unable to reorder sections",
        }
      );
    } else if (result.type === "field") {
      const fieldId = result.draggableId;

      const sourceParentId = parseInt(result.source.droppableId);
      const destParentId = parseInt(result.destination.droppableId);

      if (sourceParentId === destParentId) {
        const reorderedFields = swapElements(
          fields.filter((f) => f.sectionId === parseInt(result.source.droppableId)),
          sourceIndex,
          destIndex
        );
        void snackpatch(
          updateFormFields({
            fields: reorderedFields.map((f, i) => ({
              fieldId: f.id,
              values: {
                rank: i,
              },
            })),
          }),
          {
            successMessage: "Fields reordered successfully",
            errorMessage: "Unable to reorder fields",
          }
        );
      } else {
        const sectionId = parseInt(result.destination.droppableId);
        setDisplayFields(
          fields.map((f) => {
            if (f.id === parseInt(fieldId)) {
              return {
                ...f,
                sectionId: sectionId,
                rank: fields.filter((f) => f.sectionId === sectionId).length,
              };
            }
            return f;
          })
        );
        void snackpatch(
          updateFormField({
            id: fieldId,
            values: {
              sectionId: sectionId,
              rank: fields.filter((f) => f.sectionId === sectionId).length,
            },
          }),
          {
            successMessage: "Field moved to section",
            errorMessage: "Unable to move field to section",
          }
        );
      }
    }
  };

  function handlePageChange(evt) {
    setPageFilter(evt.target.value);
  }

  const filteredSections = displaySections.filter((section) => pageFilter === -1 || section.formPageId === pageFilter);

  return (
    <Card sx={{ p: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {editSection !== undefined && (
        <EditSectionDialog open={true} close={() => setEditSection(undefined)} section={editSection} />
      )}
      {showNewFormSection && formPages[0] !== undefined && (
        <NewFormSectionDialog
          open={true}
          close={() => setShowNewFormSection(false)}
          formPageId={pageFilter === -1 ? formPages[0].id : pageFilter}
        />
      )}
      {showNewFieldDialogSectionId !== undefined && (
        <NewFormFieldDialog
          open={true}
          close={() => setShowNewFieldDialogSectionId(undefined)}
          sectionId={showNewFieldDialogSectionId}
        />
      )}
      <EditPagesDialog open={showEditPagesDialog} onClose={() => setShowEditPagesDialog(false)} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
        <Box sx={{ pt: 1 }}>
          <FormControl>
            <InputLabel id="form-page-select">Page</InputLabel>
            <PSelect
              labelId="form-page-select"
              label="Page"
              sx={{ minWidth: "200px" }}
              value={pageFilter}
              onChange={handlePageChange}>
              <MenuItem value={-1}>All</MenuItem>
              {formPages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.name} ({sections.filter((section) => section.formPageId === page.id).length})
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
          <Tooltip title="Edit Pages">
            <IconButton onClick={() => setShowEditPagesDialog(true)}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>

        <Tooltip title="Create Section">
          <IconButton onClick={() => setShowNewFormSection(true)}>
            <Add />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
        }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable" type="section">
            {(provided) => (
              <div ref={provided.innerRef}>
                {filteredSections.map((section) => {
                  const filteredFields = displayFields.filter((f) => {
                    const hasAiConfig = fieldAiConfigs.find((aic) => aic.fieldId === f.id) !== undefined;
                    return f.sectionId === section.id && (!aiMode || hasAiConfig);
                  });
                  if (filteredFields.length === 0 && aiMode) return null;
                  return (
                    <Draggable key={section.id} draggableId={section.id.toString()} index={section.rank}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}>
                          <div>
                            <Box
                              sx={{
                                borderRadius: 1,
                                pl: 1,
                                pr: 0.5,
                                py: 1,
                                backgroundColor: "white",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}>
                              <Box>
                                <P
                                  onClick={() => navigate(`sections/${section.id}`)}
                                  variant="baseBold"
                                  sx={{
                                    ...theme.mixins.linkLike,
                                    color: "inherit",
                                    textDecoration: sectionId === section.id ? "underline" : "none",
                                  }}>
                                  {section.name}
                                </P>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}>
                                <Tooltip title="Create Field ">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      handleOpenNewField(event, section.id);
                                    }}>
                                    <Add />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Section">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      handleEditSection(event, section);
                                    }}>
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <IconButton size="small">
                                  <span {...provided.dragHandleProps}>
                                    <DragIndicator />
                                  </span>
                                </IconButton>
                              </Box>
                            </Box>
                            <SectionFieldsList fields={filteredFields} sectionId={section.id} />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Card>
  );
};

export default ProfileBuilderFieldOutline;
